import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Phone, Menu, X, ChevronDown, MapPin } from "lucide-react";
import { trackPhoneCall } from "@/lib/googleAds";

const SERVICE_AREAS = [
  { name: "Santa Barbara", slug: "santa-barbara" },
  { name: "Ventura", slug: "ventura" },
  { name: "Montecito", slug: "montecito" },
  { name: "Goleta", slug: "goleta" },
  { name: "Carpinteria", slug: "carpinteria" },
  { name: "Ojai", slug: "ojai" },
  { name: "Camarillo", slug: "camarillo" },
  { name: "Solvang", slug: "solvang" },
  { name: "Buellton", slug: "buellton" },
  { name: "Santa Ynez", slug: "santa-ynez" },
  { name: "Isla Vista", slug: "isla-vista" },
  { name: "Summerland", slug: "summerland" },
  { name: "Oxnard", slug: "oxnard" },
  { name: "Los Angeles", slug: "los-angeles" },
];

interface HeaderProps {
  onBlogClick?: () => void;
}

export default function Header({ onBlogClick }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [location] = useLocation();
  const [serviceAreasOpen, setServiceAreasOpen] = useState(false);
  const [mobileServiceAreasOpen, setMobileServiceAreasOpen] = useState(false);
  const serviceAreasRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (serviceAreasRef.current && !serviceAreasRef.current.contains(event.target as Node)) {
        setServiceAreasOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setMobileServiceAreasOpen(false);
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const scrollToSection = (id: string) => {
    if (id === 'blog') {
      if (onBlogClick) {
        onBlogClick();
      }
      return;
    }
    
    // If on home page, scroll to section
    if (location === '/') {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      // If on other page, navigate to home with hash and scroll after navigation
      window.location.href = `/#${id}`;
    }
    setMobileMenuOpen(false);
  };

  const handleBlogClick = () => {
    if (location === '/blog' || location.startsWith('/blog/')) {
      // Already on blog, scroll to top
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      // Navigate to blog
      window.location.href = '/blog';
    }
    setMobileMenuOpen(false);
  };

  const showBackground = scrolled;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          showBackground ? "bg-[#0a1628]/95 backdrop-blur-md shadow-lg" : "bg-transparent"
        }`}
      >
        <nav className="container flex items-center justify-between py-3 md:py-4">
          <Link href="/" className="flex items-center flex-shrink-0">
            <img 
              src="https://files.manuscdn.com/user_upload_by_module/session_file/310519663313033271/KuIXmYeHtVqHONwe.png" 
              alt="Danis Painting Co. — Award-Winning Painting Contractors in Santa Barbara & Ventura" 
              className="h-10 sm:h-12 md:h-14 w-auto max-w-[180px] sm:max-w-[220px] md:max-w-none object-contain"
              fetchPriority="high"
              width="220" height="56"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {["Home", "About", "Services", "Gallery", "Testimonials", "Blog", "Contact"].map((item) => {
              if (item === "Services") {
                return (
                  <Link
                    key={item}
                    href="/services"
                    className="text-white/90 hover:text-white transition-colors text-sm font-medium whitespace-nowrap"
                  >
                    {item}
                  </Link>
                );
              }
              return (
                <button
                  key={item}
                  onClick={() => item === "Blog" ? handleBlogClick() : scrollToSection(item.toLowerCase())}
                  className="text-white/90 hover:text-white transition-colors text-sm font-medium whitespace-nowrap"
                >
                  {item}
                </button>
              );
            })}
            
            {/* Service Areas Dropdown */}
            <div ref={serviceAreasRef} className="relative">
              <button
                onClick={() => setServiceAreasOpen(!serviceAreasOpen)}
                className="text-white/90 hover:text-white transition-colors text-sm font-medium flex items-center gap-1"
              >
                Service Areas
                <ChevronDown className="w-4 h-4" />
              </button>
              {serviceAreasOpen && (
                <div className="absolute top-full mt-2 left-0 bg-[#0a1628]/95 backdrop-blur-md rounded-lg shadow-lg py-2 min-w-[200px] border border-white/10">
                  {SERVICE_AREAS.map((area) => (
                    <Link key={area.slug} href={`/city/${area.slug}`} className="block px-4 py-2 text-white/80 hover:text-white hover:bg-white/10 transition-colors text-sm">
                      {area.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <Button
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 bg-transparent text-sm"
              onClick={() => scrollToSection("contact")}
            >
              Get Quote
            </Button>
            <Button
              className="bg-[#4caf30] hover:bg-[#3d9426] text-white font-semibold text-sm"
              onClick={() => {
                trackPhoneCall();
                window.location.href = "tel:805-403-8727";
              }}
            >
              <Phone className="w-4 h-4 mr-2" />
              Call Now
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white p-2 relative z-[60]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            <div className="relative w-6 h-6">
              <Menu className={`w-6 h-6 absolute inset-0 transition-all duration-300 ${mobileMenuOpen ? 'opacity-0 rotate-90 scale-75' : 'opacity-100 rotate-0 scale-100'}`} />
              <X className={`w-6 h-6 absolute inset-0 transition-all duration-300 ${mobileMenuOpen ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-75'}`} />
            </div>
          </button>
        </nav>
      </header>

      {/* Mobile Menu Overlay - OUTSIDE header to avoid stacking context issues */}
      <div
        className={`lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-[55] transition-opacity duration-300 ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Menu Slide-in Panel - OUTSIDE header to avoid stacking context issues */}
      <div
        className={`lg:hidden fixed top-0 right-0 h-full w-[85%] max-w-[360px] bg-[#0a1628] z-[60] shadow-2xl transform transition-transform duration-300 ease-out ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full overflow-y-auto">
          {/* Menu Header */}
          <div className="flex items-center justify-between p-5 border-b border-white/10">
            <img
              src="https://files.manuscdn.com/user_upload_by_module/session_file/310519663313033271/KuIXmYeHtVqHONwe.png"
              alt="Danis Painting Co. — Painting Contractors Santa Barbara & Ventura"
              className="h-10 w-auto object-contain"
              loading="lazy"
              width="160" height="40"
            />
            <button
              className="text-white/70 hover:text-white p-1 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation Links */}
          <div className="flex-1 py-4">
            <div className="px-5 space-y-1">
              {["Home", "About", "Services", "Gallery", "Testimonials", "Blog", "Contact"].map((item, index) => {
                if (item === "Services") {
                  return (
                    <Link
                      key={item}
                      href="/services"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block text-white/90 hover:text-white hover:bg-white/5 transition-all py-3 px-3 rounded-lg text-base font-medium"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      {item}
                    </Link>
                  );
                }
                return (
                  <button
                    key={item}
                    onClick={() => item === "Blog" ? handleBlogClick() : scrollToSection(item.toLowerCase())}
                    className="block w-full text-white/90 hover:text-white hover:bg-white/5 transition-all text-left py-3 px-3 rounded-lg text-base font-medium"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    {item}
                  </button>
                );
              })}
            </div>

            {/* Service Areas Section */}
            <div className="mt-4 border-t border-white/10 pt-4 px-5">
              <button
                onClick={() => setMobileServiceAreasOpen(!mobileServiceAreasOpen)}
                className="flex items-center justify-between w-full text-white/70 hover:text-white transition-colors py-2 px-3 text-sm font-semibold uppercase tracking-wider"
              >
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Service Areas
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileServiceAreasOpen ? 'rotate-180' : ''}`} />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ease-out ${mobileServiceAreasOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="grid grid-cols-2 gap-1 pt-2 pb-2">
                  {SERVICE_AREAS.map((area) => (
                    <Link
                      key={area.slug}
                      href={`/city/${area.slug}`}
                      onClick={() => setMobileMenuOpen(false)}
                      className="text-white/70 hover:text-white hover:bg-white/5 transition-all py-2 px-3 rounded-lg text-sm"
                    >
                      {area.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom CTA Section */}
          <div className="p-5 border-t border-white/10 space-y-3">
            <Button
              className="bg-[#4caf30] hover:bg-[#3d9426] text-white font-semibold w-full h-12 text-base"
              onClick={() => {
                trackPhoneCall();
                window.location.href = "tel:805-403-8727";
              }}
            >
              <Phone className="w-5 h-5 mr-2" />
              Call Now: 805-403-8727
            </Button>
            <Button
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 bg-transparent w-full h-12 text-base"
              onClick={() => {
                scrollToSection("contact");
                setMobileMenuOpen(false);
              }}
            >
              Get Free Quote
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
